import { z } from "zod"
import { ChannelEnum } from "../campaigns/commonTypes"
import {
  ConversationStatusEnum,
  ConversationTicketIntegrationKindEnum,
  ConversationTypeEnum,
  MessageFormatEnum,
  TicketStatusEnum,
} from "../conversations/response_data"
import { CompanyClientResponseDataSchema } from "../customers/response_data"

export const IssueStatusEnum = ["Open", "Reopened", "Closed", "Resolved"] as const
export const IssuePreviousStatusEnum = ["Closed", "Resolved"] as const

export const ConversationTicketIntegrationResponseDataSchema = z.object({
  kind: z.enum(ConversationTicketIntegrationKindEnum),
  externalId: z.string(),
  createdAt: z.date().transform((value) => value.toISOString()),
})

export const SupportConversationTicketResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  conversationId: z.bigint().transform(String),
  status: z.enum(TicketStatusEnum),
  createdAt: z.date().transform((value) => value.toISOString()),
  updatedAt: z.date().transform((value) => value.toISOString()),
  integrations: z.array(ConversationTicketIntegrationResponseDataSchema),
})

export const SupportConversationTicketsResponseDataSchema = z.array(
  SupportConversationTicketResponseDataSchema.extend(
    z.object({
      hasHumanReply: z.boolean(),
      hasUnansweredCustomerReply: z.boolean(),
      conversation: z.object({
        id: z.bigint().transform(String),
        type: z.enum(ConversationTypeEnum),
        status: z.enum(ConversationStatusEnum),
        orderId: z.string().nullable().optional(),
      }),
      customer: z.object({
        id: z.bigint().transform(String),
        name: z.string().optional().nullable(),
        firstName: z.string().nullish(),
        lastName: z.string().nullish(),
        email: z.string().nullish(),
        tentativeEmail: z.string().nullish(),
        client: CompanyClientResponseDataSchema.nullish(),
      }),
      campaign: z
        .object({
          id: z.bigint().transform(String),
          name: z.string().nullable(),
        })
        .optional()
        .nullable(),
    }).shape,
  ),
)

export const SupportConversationQuestionResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  conversationId: z.bigint().transform(String),
  conversationTicketId: z.bigint().transform(String).nullish(),
  summary: z.string(),
  createdAt: z.date().transform((value) => value.toISOString()),
  updatedAt: z.date().transform((value) => value.toISOString()),
  resolvedAutomaticallyAt: z
    .date()
    .transform((value) => value.toISOString())
    .nullish(),
})

export const SupportConversationQuestionsResponseDataSchema = z.array(
  SupportConversationQuestionResponseDataSchema.extend(
    z.object({
      conversation: z.object({
        id: z.bigint().transform(String),
        type: z.enum(ConversationTypeEnum),
        status: z.enum(ConversationStatusEnum),
        orderId: z.string().nullable().optional(),
      }),
      customer: z.object({
        id: z.bigint().transform(String),
        name: z.string().optional().nullable(),
        firstName: z.string().nullish(),
        lastName: z.string().nullish(),
        email: z.string().nullish(),
        tentativeEmail: z.string().nullish(),
        client: CompanyClientResponseDataSchema.nullish(),
      }),
      campaign: z
        .object({
          id: z.bigint().transform(String),
          name: z.string().nullable(),
        })
        .optional()
        .nullable(),
    }).shape,
  ),
)

export const IssueUrgencyEnum = {
  Low: "Low",
  Medium: "Medium",
  High: "High",
} as const

export const ActionRequestStatusEnum = {
  Pending: "Pending",
  Fulfilled: "Fulfilled",
  Dismissed: "Dismissed",
} as const

export const ActionRequestEligibilityEnum = {
  Unknown: "Unknown",
  Eligible: "Eligible",
  NotEligible: "NotEligible",
} as const

export const IssueIncidentReferenceSchema = z.object({
  kind: z.string().min(1),
  namespace: z.string().min(1),
  value: z.string().min(1),
})

const CustomerIssueUpdateConversationResponseDataSchema = z
  .object({
    channel: z.enum(ChannelEnum),
    event: z.unknown().nullable(),
    campaign: z
      .object({
        name: z.string().nullable(),
        channel: z.enum(ChannelEnum).nullable(),
      })
      .nullable(),
  })
  .transform(({ channel, event, campaign }) => ({
    channel,
    campaign,
    subject: getConversationEventSubject(event),
  }))

export const CustomerIssueUpdateResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  sequence: z.number(),
  summary: z.string().nullable(),
  isResolution: z.boolean(),
  resolutionStatus: z.enum(IssuePreviousStatusEnum).nullable(),
  isReopening: z.boolean(),
  previousStatus: z.enum(IssuePreviousStatusEnum).nullable(),
  employeeId: z
    .bigint()
    .nullable()
    .transform((value) => value?.toString() ?? null),
  employee: z.object({ id: z.bigint().transform(String), name: z.string() }).nullable(),
  conversationId: z
    .bigint()
    .nullable()
    .transform((value) => value?.toString() ?? null),
  conversation: CustomerIssueUpdateConversationResponseDataSchema.nullable(),
  createdAt: z.date().transform((value) => value.toISOString()),
})

export const CustomerIssueResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  subject: z.string(),
  summary: z.string(),
  urgency: z.enum(IssueUrgencyEnum),
  status: z.enum(IssueStatusEnum),
  qualifiedAt: z
    .date()
    .nullable()
    .transform((value) => value?.toISOString() ?? null),
  category: z.string().nullable(),
  media: z.array(
    z.object({
      messageId: z.bigint().transform(String),
      format: z.enum(MessageFormatEnum),
      url: z.string(),
      conversationId: z.bigint().transform(String),
      conversation: CustomerIssueUpdateConversationResponseDataSchema,
      createdAt: z.date().transform((value) => value.toISOString()),
    }),
  ),
  createdAt: z.date().transform((value) => value.toISOString()),
  updatedAt: z.date().transform((value) => value.toISOString()),
  updates: z.array(CustomerIssueUpdateResponseDataSchema),
  integrations: z.array(ConversationTicketIntegrationResponseDataSchema),
})

export const CustomerIssuesResponseDataSchema = z.array(CustomerIssueResponseDataSchema)

export const ConversationSupportQuestionItemResponseDataSchema = z.object({
  kind: z.literal("question"),
  id: z.bigint().transform(String),
  summary: z.string(),
  answeredAt: z
    .date()
    .nullable()
    .transform((value) => value?.toISOString() ?? null),
  isKnowledgeGap: z.boolean().nullable(),
  createdAt: z.date().transform((value) => value.toISOString()),
})

export const ConversationSupportActionRequestItemResponseDataSchema = z.object({
  kind: z.literal("actionRequest"),
  id: z.bigint().transform(String),
  summary: z.string(),
  status: z.enum(ActionRequestStatusEnum),
  eligibility: z.enum(ActionRequestEligibilityEnum),
  createdAt: z.date().transform((value) => value.toISOString()),
})

export const ConversationSupportIssueUpdateItemResponseDataSchema = z.object({
  kind: z.literal("issueUpdate"),
  id: z.bigint().transform(String),
  summary: z.string().nullable(),
  isResolution: z.boolean(),
  resolutionStatus: z.enum(IssuePreviousStatusEnum).nullable(),
  isReopening: z.boolean(),
  previousStatus: z.enum(IssuePreviousStatusEnum).nullable(),
  createdAt: z.date().transform((value) => value.toISOString()),
  issue: z.object({
    id: z.bigint().transform(String),
    subject: z.string(),
    urgency: z.enum(IssueUrgencyEnum),
    category: z.string().nullable(),
    status: z.enum(IssueStatusEnum),
  }),
})

export const ConversationSupportItemResponseDataSchema = z.discriminatedUnion("kind", [
  ConversationSupportQuestionItemResponseDataSchema,
  ConversationSupportActionRequestItemResponseDataSchema,
  ConversationSupportIssueUpdateItemResponseDataSchema,
])

export const ConversationSupportItemsResponseDataSchema = z.array(
  ConversationSupportItemResponseDataSchema,
)

const SupportListConversationResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  type: z.enum(ConversationTypeEnum),
  status: z.enum(ConversationStatusEnum),
  orderId: z.string().nullable().optional(),
})

const SupportListCustomerResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  name: z.string().optional().nullable(),
  email: z.string().nullish(),
  tentativeEmail: z.string().nullish(),
  client: CompanyClientResponseDataSchema.nullish(),
})

const SupportListCampaignResponseDataSchema = z
  .object({
    id: z.bigint().transform(String),
    name: z.string().nullable(),
  })
  .nullable()

const SupportListContextSchema = z.object({
  conversation: SupportListConversationResponseDataSchema,
  customer: SupportListCustomerResponseDataSchema,
  campaign: SupportListCampaignResponseDataSchema,
})

export const SupportQuestionResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  conversationId: z.bigint().transform(String),
  summary: z.string(),
  category: z.string().nullable(),
  answeredAt: z
    .date()
    .nullable()
    .transform((value) => value?.toISOString() ?? null),
  isKnowledgeGap: z.boolean().nullable(),
  createdAt: z.date().transform((value) => value.toISOString()),
  updatedAt: z.date().transform((value) => value.toISOString()),
})

export const SupportQuestionsResponseDataSchema = z.array(
  SupportQuestionResponseDataSchema.extend(SupportListContextSchema.shape),
)

export const SupportActionRequestResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  conversationId: z.bigint().transform(String),
  summary: z.string(),
  category: z.string().nullable(),
  status: z.enum(ActionRequestStatusEnum),
  eligibility: z.enum(ActionRequestEligibilityEnum),
  createdAt: z.date().transform((value) => value.toISOString()),
  updatedAt: z.date().transform((value) => value.toISOString()),
})

export const SupportActionRequestsResponseDataSchema = z.array(
  SupportActionRequestResponseDataSchema.extend(SupportListContextSchema.shape),
)

export const SupportIssueResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  subject: z.string(),
  summary: z.string(),
  category: z.string().nullable(),
  urgency: z.enum(IssueUrgencyEnum),
  status: z.enum(IssueStatusEnum),
  reopeningUpdate: z
    .object({ previousStatus: z.enum(IssuePreviousStatusEnum).nullable() })
    .nullable(),
  qualifiedAt: z
    .date()
    .nullable()
    .transform((value) => value?.toISOString() ?? null),
  occurrenceCount: z.number(),
  conversationId: z
    .bigint()
    .nullable()
    .transform((value) => (value === null ? null : String(value))),
  createdAt: z.date().transform((value) => value.toISOString()),
  updatedAt: z.date().transform((value) => value.toISOString()),
})

export const SupportIssuesResponseDataSchema = z.array(
  SupportIssueResponseDataSchema.extend(
    z.object({
      conversation: SupportListConversationResponseDataSchema.nullable(),
      customer: SupportListCustomerResponseDataSchema,
      campaign: SupportListCampaignResponseDataSchema,
      integrations: z.array(ConversationTicketIntegrationResponseDataSchema),
      // Based on the conversation of the latest conversation update
      hasHumanReply: z.boolean(),
      hasUnansweredCustomerReply: z.boolean(),
    }).shape,
  ),
)

export const SupportIssueResolutionResponseDataSchema = z.object({
  id: z.bigint().transform(String),
  status: z.enum(IssueStatusEnum),
})

export const SupportGorgiasTicketActivityResponseDataSchema = z.object({
  ticket: z
    .object({
      id: z.string(),
      subject: z.string().nullable(),
      status: z.string().nullable(),
      tags: z.array(z.string()),
      channel: z.string().nullable(),
      createdAt: z.string().nullable(),
      updatedAt: z.string().nullable(),
      assignee: z.string().nullable(),
    })
    .nullable(),
  messages: z.array(
    z.object({
      id: z.string(),
      createdAt: z.string(),
      body: z.string().nullable(),
      public: z.boolean().nullable(),
      fromAgent: z.boolean().nullable(),
      sender: z.string().nullable(),
      attachmentsCount: z.number(),
    }),
  ),
  events: z.array(
    z.object({
      id: z.string(),
      type: z.string(),
      createdAt: z.string(),
      data: z.unknown().nullable(),
      userId: z.string().nullable(),
    }),
  ),
})

function getConversationEventSubject(event: unknown) {
  if (!event || typeof event !== "object" || Array.isArray(event) || !("subject" in event)) {
    return null
  }

  const subject = event.subject
  return typeof subject === "string" && subject.trim() ? subject.trim() : null
}
